import { formatDate, useGraphQLQuery } from '@betterment/js-runtime';
import { Box, Card, Heading, Icon, Link, Separator, Shimmer, Table, Text } from '@betterment/samba-react';

import { GeneratedReportsDocument } from './graphql/query.generatedReports.generated';

import { urlBuilder } from '../../../utils/urlBuilder';

const formatDateRange = (startDate: string | Date | null | undefined, endDate: string | Date | null | undefined) => {
  if (!startDate) return formatDate(endDate!, 'longOrdinalSlashed');
  if (!endDate) return formatDate(startDate, 'longOrdinalSlashed');

  return `${formatDate(startDate, 'longOrdinalSlashed')}—${formatDate(endDate, 'longOrdinalSlashed')}`;
};

const ShimmerLoader = () => {
  return (
    <Box aria-busy="true" aria-label="Loading..." role="status">
      <Table>
        <Table.Header>
          <Table.Row>
            {[...Array(4).keys()].map(key => (
              <Table.ColumnHeaderCell key={key}>
                <Shimmer
                  css={{
                    height: 'size.100',
                  }}
                />
              </Table.ColumnHeaderCell>
            ))}
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {[...Array(4).keys()].map(row => (
            <Table.Row key={row}>
              {[...Array(4).keys()].map(column => (
                <Table.Cell key={`${row}-${column}`}>
                  <Shimmer
                    css={{
                      alignItems: 'center',
                      height: 'size.200',
                      width: '70%',
                    }}
                  />
                </Table.Cell>
              ))}
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </Box>
  );
};

const ReportsTable = ({ employerId }: { employerId: string }) => {
  const { data } = useGraphQLQuery(GeneratedReportsDocument, {
    variables: { employerId },
  });

  if (!data) {
    return <ShimmerLoader />;
  }

  const isReportsEmpty = data?.employer.reports.length === 0;

  if (isReportsEmpty) {
    return <Text css={{ py: 'space.200' }}>No reports generated yet</Text>;
  }

  return (
    <Table>
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell>Date generated</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Report date</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Report name</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Download</Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {data?.employer.reports.map(report => (
          <Table.Row key={report.id}>
            <Table.Cell>{formatDate(report.createdAt, 'longOrdinalSlashed')}</Table.Cell>
            <Table.Cell>{formatDateRange(report.startDate, report.endDate)}</Table.Cell>
            <Table.Cell>{report.name}</Table.Cell>
            <Table.Cell>
              <Link
                href={urlBuilder.downloadPlanReport({ reportId: report.id })}
                download={`${report.name}.csv`}
                css={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Icon name="DocumentPopulated" size="xsmall" />
                {report.contentType}
              </Link>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
};

const ReportBlurb = () => {
  return (
    <Text as="p" color="text.weak">
      The information in these reports is current as of the specified date, is based on information provided by the Plan
      Sponsor or its representative, and does not cover any period predating the retention of Betterment at Work. These
      reports should not be substituted for a certified financial statement.
    </Text>
  );
};

export const GeneratedReports = ({ employerId }: { employerId: string }) => {
  return (
    <>
      <Box css={{ py: 'space.300' }}>
        <Card css={{ p: 'space.300', gap: 'space.300' }}>
          <Card.Header>
            <Heading as="h2" variant="title200">
              Generated reports
            </Heading>
          </Card.Header>
          <Separator />
          <Card.Content>
            <ReportsTable employerId={employerId} />
          </Card.Content>
        </Card>
      </Box>

      <ReportBlurb />
    </>
  );
};
